import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Modal,
  Alert, // Added Alert for error display
  Spinner, // Added Spinner for loading display
} from "react-bootstrap";
import {
  FaPlus,
  FaMinus,
  FaTrash,
  FaArrowLeft,
  FaWhatsapp,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "./orderpage.css";
import { CartContext } from "./CartContext";
import { v4 as uuidv4 } from "uuid";
// NOTE: Removed 'websiteDetails' import as we are fetching data dynamically
import API_DOMAIN from "../config/config";

const OrderPage = () => {
  const { cart, increaseQty, decreaseQty, removeItem, clearCart } =
    useContext(CartContext);

  // ----------- FORM STATE & VALIDATION -----------
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [phoneError, setPhoneError] = useState(""); // New state for phone validation

  // ----------- API & MODAL STATE -----------
  const [showPopup, setShowPopup] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyDetails, setCompanyDetails] = useState(null); // New state for dynamic company info
  console.log("Company Details:", companyDetails);
  const [companyLoading, setCompanyLoading] = useState(true);

  const navigate = useNavigate();

  // ------------------ HANDLERS ------------------

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits and limit length to 10
    const filteredValue = value.replace(/\D/g, "").slice(0, 10);
    setPhone(filteredValue);

    // Clear error immediately if it meets 10 digits or is empty
    if (filteredValue.length === 10 || filteredValue.length === 0) {
      setPhoneError("");
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const closePopup = () => {
    setShowPopup(false);
    setName("");
    setPhone("");
    setAddress("");
    navigate("/");
  };

  // ------------------ DATA FETCHING (Company Details + Products) ------------------

  useEffect(() => {
    const fetchCompanyAndProducts = async () => {
      const companyId = "COMP-000001";

      // 1. Fetch Company Details
      setCompanyLoading(true);
      try {
        const companyResponse = await fetch(`${API_DOMAIN}/company.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ search_text: "" }),
        });

        const companyData = await companyResponse.json();
        if (companyData.head.code === 200 && companyData.body.companies) {
          setCompanyDetails(companyData.body.companies[0]);
        } else {
          console.error(
            "Failed to load company details:",
            companyData.head.msg
          );
        }
      } catch (e) {
        console.error("Error fetching company:", e);
      } finally {
        setCompanyLoading(false);
      }
      setLoading(true);
      try {
      } catch (e) {
        // handle error if necessary
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyAndProducts();
  }, []);

  // ------------------ ORDER CALCULATION ------------------
  const subTotal = cart.reduce((sum, item) => sum + item.price * item.qty, 0);
  const discount = 0;
  const otherCharges = 0;
  const total = subTotal + otherCharges;
  const totalQuantity = cart.reduce((sum, item) => sum + item.qty, 0);

  // ------------------ PLACE ORDER LOGIC ------------------
  const handlePlaceOrder = async (e) => {
    e.preventDefault();

    // 1. Local Validation Check (Name, Address, Cart Empty)
    if (cart.length === 0 || !name || !address) {
      alert(
        "Please fill in all customer details and ensure your cart is not empty."
      );
      return;
    }

    // 2. Phone Validation Check (10 digits)
    if (phone.length !== 10) {
      setPhoneError("Mobile number must be exactly 10 digits.");
      return;
    }
    setPhoneError(""); // Clear error if validation passes

    setIsSubmitting(true);
    setError(null);

    const dynamicCompanyId = companyDetails.company_id;
    console.log("Using Company ID for Order:", dynamicCompanyId);
    const orderPayload = {
      company_id: dynamicCompanyId,
      order_no: `ORD-${uuidv4().slice(0, 6).toUpperCase()}`,
      customer_details: JSON.stringify({
        name: name,
        phone: phone,
        address: address,
      }),
      product_details: JSON.stringify(
        cart.map((item) => {
          return {
            product_code: item.product_code,
            product_name: item.product_name,
            product_id: item.product_id,
            price: item.price.toFixed(2),
            qty: item.qty,
            line_total: (item.price * item.qty).toFixed(2),
          };
        })
      ),
      sub_total: subTotal.toFixed(2),
      discount: discount.toFixed(2),
      other_charges: otherCharges.toFixed(2),
      grand_total: total.toFixed(2),
      user_id: 1,
      created_name: "Customer Order",
    };

    try {
      const response = await fetch(`${API_DOMAIN}/order.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderPayload),
      });

      const data = await response.json();

      if (data.head.code === 200) {
        const apiOrderId = data.body.order_no || orderPayload.order_no;
        setOrderId(apiOrderId);
        clearCart();
        setShowPopup(true);
      } else {
        throw new Error(
          data.head.msg || "Order creation failed on the server."
        );
      }
    } catch (e) {
      console.error("Order API Error:", e);
      setError(`Failed to place order: ${e.message}`);
      alert(`Failed to place order: ${e.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const mobileFromCompany = String(companyDetails?.mobile || "");
  const companyName = companyDetails?.company_name || "Online Store";
  const contactNumber = mobileFromCompany || "N/A";
  const cleanedContactNumber = mobileFromCompany.replace(/[^\d]/g, "");
  const whatsappLink = `https://wa.me/91${cleanedContactNumber}`;

  if (companyLoading && !companyDetails) {
    return (
      <Container className="text-center py-5">
        <Spinner animation="border" />
        <p>Loading order details...</p>
      </Container>
    );
  }

  return (
    <div className="order-page-wrapper">
      <Container fluid className="py-4">
        <Row className="justify-content-center">
          <Col md={10}>
            <Row className="mb-3">
              <Col>
                <Button
                  variant="link"
                  onClick={handleGoBack}
                  className="p-2 text-white bg-primary"
                  style={{ fontSize: "1rem" }}
                >
                  <FaArrowLeft className="me-2" />
                </Button>
              </Col>
            </Row>
            <Row>
              <Col md={8}>
                <Card className="mb-3 p-3 custom-card">
                  <Row>
                    <Col md={8}>
                      <h5>
                        <b>Order Summary</b>
                      </h5>
                    </Col>
                    <Col md={4} className="text-end">
                      <p className="item-count">
                        {totalQuantity > 0
                          ? `${totalQuantity} items`
                          : "0 items"}
                      </p>
                    </Col>
                  </Row>
                  <hr className="summary-divider" />
                  {cart.length === 0 ? (
                    <p>Your cart is empty.</p>
                  ) : (
                    cart.map((item) => (
                      <Row
                        key={item.id}
                        className="align-items-center mb-3 product-item"
                      >
                        <Col xs={2}>
                          <img
                            src={item.product_img_url || item.image}
                            alt={item.product_name}
                            className="order-product-img"
                          />
                        </Col>
                        <Col xs={6}>
                          <h6>{item.product_name}</h6>
                          <strong>₹ {item.price.toFixed(2)}</strong>
                          <div>
                            <Button
                              variant="link"
                              className="text-danger p-0 mt-1 remove-btn"
                              onClick={() => removeItem(item.id)}
                            >
                              <FaTrash /> Remove
                            </Button>
                          </div>
                        </Col>
                        <Col xs={4} className="text-end">
                          <div className="quantity-controls">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => decreaseQty(item.id)}
                            >
                              –
                            </Button>
                            <span className="qty-count mx-2">{item.qty}</span>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => increaseQty(item.id)}
                            >
                              +
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    ))
                  )}
                </Card>
              </Col>

              {/* Delivery & Price Details */}
              <Col md={4}>
                {/* Delivery Address */}
                <Card className="mb-3 p-3 custom-card">
                  <h6>
                    <b>Delivery Address</b>
                  </h6>
                  <Form onSubmit={handlePlaceOrder}>
                    <Form.Group className="mb-2">
                      <Form.Control
                        placeholder="Name *"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Control
                        type="tel"
                        placeholder="+91 Phone *"
                        value={phone}
                        maxLength={10}
                        onChange={handlePhoneChange}
                        required
                      />
                      {phoneError && (
                        <Form.Text className="text-danger">
                          {phoneError}
                        </Form.Text>
                      )}
                    </Form.Group>
                    <Form.Group>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Address"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Form>
                </Card>

                {/* Price Details */}
                <Card className="mb-3 p-3 custom-card">
                  <h6>
                    <b>Price Details</b>
                  </h6>
                  <div className="d-flex justify-content-between">
                    <span>Sub Total</span>
                    <span>₹ {subTotal.toFixed(2)}</span>
                  </div>
                  {/* <div className="d-flex justify-content-between">
                    <span>Discounts</span>
                    <span>- ₹ 0.00</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Other Charges</span>
                    <span>₹ {otherCharges.toFixed(2)}</span>
                  </div> */}
                  <hr />
                  <div className="d-flex justify-content-between fw-bold">
                    <span>To Pay</span>
                    <span>₹ {total.toFixed(2)}</span>
                  </div>
                  {/* <div
                    style={{
                      color: "green",
                      fontSize: "12px",
                      marginTop: "5px",
                    }}
                  >
                    Add items worth ₹ {Math.max(845 - subTotal, 0).toFixed(2)}{" "}
                    more to place an order
                  </div> */}
                </Card>

                {/* Place Order Button (now triggers handlePlaceOrder with validation) */}
                <Button
                  variant="secondary"
                  className="w-100"
                  onClick={handlePlaceOrder}
                  disabled={
                    cart.length === 0 ||
                    !name ||
                    !phone ||
                    !address ||
                    isSubmitting
                  }
                >
                  {isSubmitting ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Placing Order...
                    </>
                  ) : (
                    <>
                      Place Order <br />
                      <span style={{ fontSize: "12px" }}>
                        COD (Cash On Delivery)
                      </span>
                    </>
                  )}
                </Button>
                {error && (
                  <Alert variant="danger" className="mt-3">
                    {error}
                  </Alert>
                )}
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Popup for Order Confirmation (DYNAMICALLY UPDATED) */}
        <Modal show={showPopup} onHide={closePopup} centered>
          <Modal.Header closeButton>
            <Modal.Title></Modal.Title>
          </Modal.Header>
          <Modal.Body className="popup-content">
            <img
              src="https://vyaparwebsiteimages.vypcdn.in/catalogue/done-large.svg?w=64&q=75"
              alt="Tick Icon"
              className="tick-icon"
            />
            <h4 className="order-placed">Order Placed!</h4>
            <p className="order-id">Your Order ID is {orderId}.</p>
            <p className="order-success">
              Your order was placed successfully from <b>{companyName}</b>
            </p>

            <Row className="contact-row">
              <Col xs={6} className="contact-col">
                <p className="contact-box">
                  <a href={`tel:${contactNumber}`} className="contact-link">
                    📞 {contactNumber}
                  </a>
                </p>
              </Col>
              <Col xs={6} className="contact-col">
                <p className="contact-box">
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-link"
                  >
                    <FaWhatsapp size={20} color="#25D366" /> Send WhatsApp
                  </a>
                </p>
              </Col>
            </Row>
            <hr className="popup-divider" />
            <p>
              <b>{companyName}</b> and 1 Lakh+ users are using our site to get
              orders online.
            </p>
            {/* <Button
              variant="danger"
              className="w-50"
              onClick={closePopup}
              style={{ backgroundColor: "#ff0000", borderColor: "#ff0000" }}
            >
              Download Vyapar App
            </Button> */}
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};

export default OrderPage;
